import {
  FileText,
  Image,
  Table,
  File,
  Loader2,
  Copy,
  Check,
} from "lucide-react";
import { Sheet, Badge, type BadgeColor } from "@ui";
import type { Document } from "@agentic_system/api/document";
import { useDocument, formatFileSize } from "@agentic_system/api/document";
import { useState } from "react";

interface DocumentDrawerProps {
  documentId: number | null;
  onClose: () => void;
}

const STATUS_CONFIG: Record<
  Document["ocr_status"],
  { label: string; color: BadgeColor }
> = {
  pending: { label: "En attente", color: "default" },
  processing: { label: "En cours", color: "blue" },
  completed: { label: "Prêt", color: "green" },
  failed: { label: "Échoué", color: "red" },
  skipped: { label: "Ignoré", color: "yellow" },
};

function getFileIcon(contentType: string) {
  if (contentType.startsWith("image/")) return Image;
  if (contentType === "application/pdf") return FileText;
  if (contentType.includes("wordprocessingml")) return FileText;
  if (contentType.includes("spreadsheetml")) return Table;
  return File;
}

export function DocumentDrawer({ documentId, onClose }: DocumentDrawerProps) {
  const { data: document, isLoading } = useDocument(documentId ?? 0);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (document?.ocr_content) {
      await navigator.clipboard.writeText(document.ocr_content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const Icon = document ? getFileIcon(document.content_type) : File;

  return (
    <Sheet
      open={documentId !== null}
      onClose={onClose}
      title={document?.filename ?? "Chargement..."}
      subtitle={document ? formatFileSize(document.byte_size) : undefined}
      width="lg"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="size-8 animate-spin text-gray-400" />
        </div>
      ) : document ? (
        <div className="space-y-6">
          {/* Document info */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <Icon className="size-8 text-gray-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {document.filename}
              </p>
              <p className="text-sm text-gray-500">{document.content_type}</p>
            </div>
            <Badge
              label={STATUS_CONFIG[document.ocr_status].label}
              color={STATUS_CONFIG[document.ocr_status].color}
            />
          </div>

          {/* Extracted text */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900">
                Texte extrait
              </h3>
              {document.ocr_content && (
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
                >
                  {copied ? (
                    <>
                      <Check className="size-3.5" />
                      Copié !
                    </>
                  ) : (
                    <>
                      <Copy className="size-3.5" />
                      Copier
                    </>
                  )}
                </button>
              )}
            </div>

            {document.ocr_status === "completed" && document.ocr_content ? (
              <div className="bg-gray-50 rounded-lg p-4 max-h-[60vh] overflow-y-auto">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                  {document.ocr_content}
                </pre>
              </div>
            ) : document.ocr_status === "processing" ? (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <Loader2 className="size-6 animate-spin text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  Extraction du texte en cours...
                </p>
              </div>
            ) : document.ocr_status === "pending" ? (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <p className="text-sm text-gray-500">
                  Extraction du texte en attente.
                </p>
              </div>
            ) : document.ocr_status === "failed" ? (
              <div className="bg-red-50 rounded-lg p-8 text-center">
                <p className="text-sm text-red-600">
                  Impossible d'extraire le texte de ce document.
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <p className="text-sm text-gray-500">
                  Aucun contenu textuel disponible.
                </p>
              </div>
            )}
          </div>

          {/* Stats */}
          {document.ocr_content && (
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-2xl font-semibold text-gray-900">
                  {document.ocr_content.length.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">Caractères</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-2xl font-semibold text-gray-900">
                  {document.ocr_content.split("\n").length.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">Lignes</p>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </Sheet>
  );
}
